import React from "react";
import { Avatar, Badge, Btn, Chip, ErrorState, Select, Toggle } from "../../../ForBrands/ui/BrandUI";
import { ChipScroller, ListRule, MobileCard, MobileEmpty, MobilePageHead, TextLink } from "../../../ForBrands/ui/mobile/MobileUI";

// Athlete Mode · My Circle on phones (Figma 03 · My Circle, 2000:831). The
// page keeps the data and handlers; this is only the phone tree: the head,
// the Access note, the filter chips in a side scroller, one contact card per
// person and "Add to my Circle" after the last card. People only, as on the
// web (D-a13). The controls Figma does not draw stay on the card (D-a14): the
// permission select or the rep's financial-terms toggle under ACCESS, and
// "Remove" as a muted text button beside "Message".

const cx = (...parts) => parts.filter(Boolean).join(" ");

// One meta column: overline label, then the value or control.
const MetaItem = ({ label, className = "", children }) => (
  <div className={cx("flex min-w-0 flex-1 flex-col gap-[2px] break-words", className)}>
    <p className="bm-overline">{label}</p>
    {children}
  </div>
);

// The permission select sits under ACCESS with 16px text, so iOS Safari does
// not zoom when it opens (D-g6). Its column asks for enough room to show
// "Manage profile" whole: side by side on a 390px screen, and on narrower
// ones the two columns wrap into two rows instead of clipping the value.
const SELECT_COLUMNS = { relationship: "basis-[116px]", access: "basis-[176px]" };

const ContactCard = ({ row, busy, permissions, onPermission, onShareFinancials, onRemove }) => {
  const working = busy === row.id;
  const select = row.control === "permission";
  let access = <p className="bm-body-s text-bm-ink">{row.access}</p>;
  if (select) {
    access = (
      <Select
        value={row.raw.permission || "view"}
        onChange={(e) => onPermission(row.raw, e.target.value)}
        disabled={working}
        aria-label={`Access for ${row.name}`}
        className="mt-1 !h-10 !w-full !rounded-full !pl-4 !pr-8 !text-[16px]"
      >
        {permissions.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
      </Select>
    );
  } else if (row.control === "financials") {
    access = (
      <div className="mt-1 flex items-center gap-2">
        <Toggle on={!!row.raw.shareFinancials} onChange={(on) => onShareFinancials(row.raw, on)} label="Share financial terms" />
        <span className="min-w-0 bm-body-s text-bm-ink">{row.access}</span>
      </div>
    );
  }

  return (
    <MobileCard as="li">
      <div className="flex items-center gap-3">
        <Avatar initials={row.initials} size={40} tone="muted" className="!font-semibold !text-[12px]" />
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <h2 className="bm-h-xs text-bm-ink">{row.name}</h2>
          <p className="bm-body-s text-bm-muted">{row.subtitle}</p>
        </div>
        <Badge tone="outline" className="shrink-0">
          {row.role}
        </Badge>
      </div>
      <ListRule />
      <div className="flex flex-wrap gap-3">
        <MetaItem label="Relationship" className={select ? SELECT_COLUMNS.relationship : ""}>
          <p className="bm-body-s text-bm-ink">{row.relationship}</p>
        </MetaItem>
        <MetaItem label="Access" className={select ? SELECT_COLUMNS.access : ""}>
          {access}
        </MetaItem>
      </div>
      <div className="flex items-center justify-between gap-4">
        <TextLink href={`mailto:${row.raw.email}`}>Message</TextLink>
        <button
          type="button"
          onClick={() => onRemove(row.raw)}
          disabled={working}
          aria-label={`Remove ${row.name}`}
          className="-my-2 -mr-2 p-2 bm-label-l text-bm-muted hover:text-bm-critical disabled:cursor-not-allowed disabled:opacity-60"
        >
          Remove
        </button>
      </div>
    </MobileCard>
  );
};

const CircleMobile = ({
  subtitle,
  accessNote,
  error,
  filters,
  counts,
  filter,
  onFilter,
  rows,
  emptyText,
  busy,
  permissions,
  onPermission,
  onShareFinancials,
  onRemove,
  onAdd,
}) => (
  <div className="flex flex-col gap-[18px]">
    <MobilePageHead title="My Circle" sub={subtitle} />

    {error && <ErrorState>{error}</ErrorState>}

    <MobileCard className="!gap-[10px]">
      <Badge tone="outline" className="self-start">
        Access
      </Badge>
      <p className="bm-body-s text-bm-muted">{accessNote}</p>
    </MobileCard>

    <ChipScroller>
      {filters.map((f) => (
        <Chip key={f.value} active={filter === f.value} count={counts[f.value]} onClick={() => onFilter(f.value)}>
          {f.label}
        </Chip>
      ))}
    </ChipScroller>

    {rows.length === 0 ? (
      <MobileEmpty>{emptyText}</MobileEmpty>
    ) : (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
      <ul role="list" className="flex flex-col gap-[18px]">
        {rows.map((row) => (
          <ContactCard
            key={`${row.kind}-${row.id}`}
            row={row}
            busy={busy}
            permissions={permissions}
            onPermission={onPermission}
            onShareFinancials={onShareFinancials}
            onRemove={onRemove}
          />
        ))}
      </ul>
    )}

    <Btn size="lg" className="w-full" onClick={onAdd}>
      Add to my Circle
    </Btn>
  </div>
);

export default CircleMobile;
